// TES-1034 (Emelie's point 7) — what the doctor may do with a measurement
// occasion on the patient page.
//
// Her self-reported weight (TES-999), blood pressure and resting pulse are
// hers: the doctor may ask her to correct them, or record a separate
// measurement of his own he has a real basis for, but never correct them for
// her. What
// he may still correct (TES-863) is the rest of her occasion (height, waist,
// hip) and any occasion of his own.
//
// Pure on purpose (no React, only type imports) so `node --test` can run it.
import type {
  MeasurementComponents,
  MeasurementReviewStatus,
} from "@care-by-ella/contracts";
import type { StatusTone } from "./status-tone";

export interface OccasionFacts {
  source: "patient" | "form" | "clinician" | "import";
  current: boolean;
  components: MeasurementComponents;
}

export function hasVitals(c: MeasurementComponents): boolean {
  return c.bloodPressure !== undefined || c.pulse !== undefined;
}

function isHers(o: OccasionFacts): boolean {
  return o.source !== "clinician";
}

/** Only the weight is on it; nothing on the occasion is his to correct. */
function onlyWeight(c: MeasurementComponents): boolean {
  return c.weight !== undefined && Object.keys(c).every((k) => k === "weight");
}

/** "Rätta": his correction of the occasion, beside the original. */
export function canDoctorCorrect(o: OccasionFacts): boolean {
  if (!o.current) return false;
  if (isHers(o) && hasVitals(o.components)) return false;
  return !onlyWeight(o.components);
}

/** "Be patienten rätta": a chat message asking her to correct her value. */
export function canAskPatient(o: OccasionFacts): boolean {
  return (
    o.current &&
    isHers(o) &&
    (o.components.weight !== undefined || hasVitals(o.components))
  );
}

/**
 * The pre-filled message (a medical notice since TES-1147). Weight has its
 * own correction in the app; blood pressure and pulse are corrected by
 * registering a new reading.
 */
export function askPatientMessage(
  c: MeasurementComponents,
  date: string,
): string {
  const parts: string[] = [];
  if (c.bloodPressure) {
    parts.push(
      `blodtrycket ${c.bloodPressure.systolic}/${c.bloodPressure.diastolic} mmHg`,
    );
  }
  if (c.pulse !== undefined) parts.push(`vilopulsen ${c.pulse} slag/min`);
  if (c.weight !== undefined) parts.push(`vikten ${c.weight} kg`);
  const what = parts.join(" och ");
  const lines = [
    `Hej! Värdet du registrerade ${date} (${what}) ser ut att kunna vara fel.`,
  ];
  if (hasVitals(c)) {
    lines.push(
      "Om det är fel kan du mäta igen och registrera den nya mätningen under Hälsa → Din hälsa över tid.",
    );
  }
  if (c.weight !== undefined) {
    lines.push(
      "En felaktig vikt kan du rätta under Hälsa → Din hälsa över tid → Grafer och trender → Rätta registrering.",
    );
  }
  return lines.join(" ");
}

export interface VerifiedDraft {
  measuredAt: string;
  systolic: string;
  diastolic: string;
  pulse: string;
  basis: string;
}

export const EMPTY_VERIFIED_DRAFT: VerifiedDraft = {
  measuredAt: "",
  systolic: "",
  diastolic: "",
  pulse: "",
  basis: "",
};

/**
 * "Registrera egen mätning" (TES-1056: "Mätning registrerad av läkare", never
 * "verifierad"): the draft as a request body, or the first
 * problem with it. Blood pressure is both values or neither; at least one of
 * blood pressure and pulse; a basis is required; never in the future.
 */
export function parseVerifiedDraft(
  d: VerifiedDraft,
  now: Date = new Date(),
):
  | {
      body: {
        measuredAt: string;
        basis: string;
        components: MeasurementComponents;
      };
    }
  | { problem: string } {
  const when = new Date(d.measuredAt);
  if (!d.measuredAt || Number.isNaN(when.getTime())) {
    return { problem: "Ange när mätningen gjordes." };
  }
  if (when.getTime() > now.getTime()) {
    return { problem: "Mättidpunkten kan inte ligga i framtiden." };
  }
  const components: MeasurementComponents = {};
  const sys = d.systolic.trim();
  const dia = d.diastolic.trim();
  if (sys || dia) {
    const s = Number(sys);
    const di = Number(dia);
    if (!Number.isInteger(s) || s < 50 || s > 300) {
      return { problem: "Övertrycket ska vara ett heltal mellan 50 och 300." };
    }
    if (!Number.isInteger(di) || di < 30 || di > 200) {
      return { problem: "Undertrycket ska vara ett heltal mellan 30 och 200." };
    }
    components.bloodPressure = { systolic: s, diastolic: di };
  }
  const pulse = d.pulse.trim();
  if (pulse) {
    const p = Number(pulse);
    // The contract's MEASUREMENT_BOUNDS.pulse (25–220).
    if (!Number.isInteger(p) || p < 25 || p > 220) {
      return { problem: "Vilopulsen ska vara ett heltal mellan 25 och 220." };
    }
    components.pulse = p;
  }
  if (!components.bloodPressure && components.pulse === undefined) {
    return { problem: "Ange blodtryck, vilopuls eller båda." };
  }
  if (!d.basis.trim()) {
    return { problem: "Ange underlaget för mätningen." };
  }
  return {
    body: {
      measuredAt: when.toISOString(),
      basis: d.basis.trim(),
      components,
    },
  };
}

// TES-1056 (TES-1054 point F) — the doctor's assessment of one of her blood
// pressure / pulse occasions, as a tag. Emelie:
// "Patientens värden ska inte märkas Verifierade/inte verifierade."
// So an unassessed reading is neutral, not a green "complete": it is there,
// and nobody has judged it yet. Assessed as a basis is complete; a reading
// that needs a new measurement is missing evidence (never the risk tone).
export const MEASUREMENT_REVIEW_TONE: Record<
  MeasurementReviewStatus,
  StatusTone
> = {
  patient_reported: "neutral",
  reviewed_as_basis: "complete",
  needs_new_measurement: "missing",
};

/** Whether the doctor assesses this occasion: hers, with BP or pulse. */
export function canReviewOccasion(o: {
  source: string;
  components: MeasurementComponents;
}): boolean {
  return o.source !== "clinician" && hasVitals(o.components);
}
